import { useEffect, useState, useCallback } from "react";
import API from "@/lib/api";
import ReactMarkdown from "react-markdown";
import { Loader2, FileText, ShieldAlert, Pencil, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";

export default function ForumTermsEditor() {
  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [version, setVersion] = useState("");
  const [body, setBody] = useState("");
  const [changelog, setChangelog] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    try {
      const { data } = await API.get("/forum/terms");
      setTerms(data);
    } catch {
      toast({ title: "Could not load terms", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    setVersion("");
    setBody(terms?.body_markdown || "");
    setChangelog("");
    setEditing(true);
    setShowPreview(false);
  };

  const publish = async () => {
    setSaving(true);
    try {
      await API.post("/forum/terms", {
        version: version.trim(),
        body_markdown: body,
        changelog: changelog.trim(),
      });
      toast({ title: "New forum terms published", description: "All members will be prompted to re-accept." });
      setEditing(false);
      setConfirmOpen(false);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({ title: "Couldn't publish", description: typeof detail === "string" ? detail : "Check version & body.", variant: "destructive" });
      setConfirmOpen(false);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="forum-terms-loading">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!editing) {
    return (
      <div className="space-y-4" data-testid="forum-terms-view">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-lg font-semibold">Forum Terms of Use</h3>
            <p className="text-xs text-muted-foreground">
              Version <span className="font-mono font-medium">{terms?.version}</span>
              {terms?.published_at && <> · Published {new Date(terms.published_at).toLocaleDateString()}</>}
            </p>
          </div>
          <Button onClick={startEdit} size="sm" className="rounded-full gap-1" data-testid="forum-terms-edit-button">
            <Pencil className="w-4 h-4" /> Publish update
          </Button>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 space-y-2 [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:space-y-2 [&>p]:mb-2 [&_strong]:font-semibold">
          <ReactMarkdown>{terms?.body_markdown || ""}</ReactMarkdown>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-900 flex gap-2">
          <FileText className="w-4 h-4 shrink-0 mt-0.5" />
          <span>Publishing a new version prompts every existing member to re-accept the terms before they post again. Their display names and avatars are preserved.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="forum-terms-editor">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-semibold">Publish new forum terms</h3>
          <p className="text-xs text-muted-foreground">Current version: <span className="font-mono">{terms?.version}</span></p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="rounded-full gap-1"
          onClick={() => setShowPreview(v => !v)}
          data-testid="forum-terms-preview-toggle"
        >
          <Eye className="w-4 h-4" /> {showPreview ? "Hide preview" : "Preview"}
        </Button>
      </div>

      <div>
        <Label className="text-xs font-medium">New version (e.g. 2026.03-phi-softened)</Label>
        <Input
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          placeholder="short semantic tag"
          maxLength={60}
          className="mt-1 h-10 font-mono text-sm"
          data-testid="forum-terms-version-input"
        />
        <p className="text-xs text-muted-foreground mt-1">Must differ from current.</p>
      </div>

      <div>
        <Label className="text-xs font-medium">Terms body (markdown supported)</Label>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={14}
          className="mt-1 font-mono text-xs"
          maxLength={20000}
          placeholder="**Please read carefully…**"
          data-testid="forum-terms-body-input"
        />
      </div>

      <div>
        <Label className="text-xs font-medium">Changelog (shown to members in the re-consent banner)</Label>
        <Textarea
          value={changelog}
          onChange={(e) => setChangelog(e.target.value)}
          rows={3}
          className="mt-1"
          maxLength={2000}
          placeholder="What changed? e.g. Softened PHI language, added safety bullet…"
          data-testid="forum-terms-changelog-input"
        />
      </div>

      {showPreview && (
        <div className="border-2 border-dashed border-border rounded-xl p-4 bg-muted/30" data-testid="forum-terms-preview">
          <p className="text-xs font-semibold text-muted-foreground mb-2">Preview — as members will see it:</p>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 space-y-2 [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:space-y-2 [&>p]:mb-2 [&_strong]:font-semibold">
            <ReactMarkdown>{body || "_Body preview will appear here_"}</ReactMarkdown>
          </div>
          {changelog && (
            <div className="mt-3 bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-900">
              <strong>What's new:</strong> {changelog}
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <Button variant="outline" className="flex-1 rounded-full" onClick={() => setEditing(false)} disabled={saving} data-testid="forum-terms-cancel">
          Cancel
        </Button>
        <Button
          className="flex-1 rounded-full gap-1"
          onClick={() => setConfirmOpen(true)}
          disabled={saving || !version.trim() || version.trim() === terms?.version || body.trim().length < 10}
          data-testid="forum-terms-publish"
        >
          <ShieldAlert className="w-4 h-4" /> Publish
        </Button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="forum-terms-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Publish new forum terms?</AlertDialogTitle>
            <AlertDialogDescription>
              All {terms?.version} consents will be invalidated. Every existing member will be prompted to re-accept the
              updated terms (with your changelog shown as "What's new") before they can post or reply again.
              Display names, avatars, and existing posts are preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={publish} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="forum-terms-confirm-publish">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Publish"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
